import { functions } from "@/services/firebase/firebase";
import { httpsCallable } from "firebase/functions";

import imageCompression from "browser-image-compression";

// Constants
const BYTES_PER_MB = 1024 * 1024;
const MAX_FILE_SIZE = 2 * BYTES_PER_MB;
const MAX_SIZE_BEFORE_COMPRESSION = 50 * BYTES_PER_MB;

// Cloudinary configuration
export const cloudinaryConfig = {
  cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME,
  uploadPreset: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET,
  apiKey: process.env.REACT_APP_CLOUDINARY_API_KEY,
};

// Profile picture upload configuration
export const profilePictureConfig = {
  maxFileSize: MAX_FILE_SIZE,
  allowedFormats: ["jpg", "jpeg", "png", "webp"],
  compressionOptions: {
    small: {
      maxSizeMB: 1,
      maxWidthOrHeight: 600,
      useWebWorker: true,
      fileType: "image/jpeg",
    },
    medium: {
      maxSizeMB: 1.5,
      maxWidthOrHeight: 800,
      useWebWorker: true,
      fileType: "image/jpeg",
    },
    large: {
      maxSizeMB: 2,
      maxWidthOrHeight: 1000,
      useWebWorker: true,
      fileType: "image/jpeg",
    },
    xlarge: {
      maxSizeMB: 2,
      maxWidthOrHeight: 1200,
      useWebWorker: true,
      fileType: "image/jpeg",
    },
  },
  transformation: {
    width: 400,
    height: 300,
    crop: "fill",
    gravity: "face",
    quality: "auto",
    format: "auto",
  },
};

// Upload profile picture to Cloudinary
export const uploadProfilePicture = async (file: File): Promise<string> => {
  if (!file) throw new Error("No file provided for upload");

  // Compress the image before uploading
  let compressedFile: File;
  try {
    compressedFile = await imageCompression(file, {
      maxSizeMB: 1,
      maxWidthOrHeight: 800,
      useWebWorker: true,
      fileType: "image/jpeg",
    });
  } catch (err) {
    console.warn("imageCompression failed, falling back to original file", err);
    // Fallback to original file to avoid crashing the upload flow
    compressedFile = file;
  }

  // 1. Get signature from Cloud Function
  const getSignature = httpsCallable(functions, "getCloudinarySignature");
  const { data } = await getSignature({});
  const { signature, timestamp, apiKey, cloudName, uploadPreset } = data as any;

  // 2. Prepare form data
  const formData = new FormData();
  formData.append("file", compressedFile);
  formData.append("api_key", apiKey);
  formData.append("timestamp", timestamp.toString());
  formData.append("upload_preset", uploadPreset);
  formData.append("signature", signature);

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
      {
        method: "POST",
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error("Failed to upload image");
    }

    const data = await response.json();
    return data.secure_url;
  } catch (error) {
    console.error("Error uploading profile picture:", error);
    throw error;
  }
};

export type ImageValidationError = "invalidFormat" | "tooLarge";

export const validateImageFile = (
  file: File
): ImageValidationError | null => {
  const fileExtension = file.name.split(".").pop()?.toLowerCase();

  if (
    !fileExtension ||
    !profilePictureConfig.allowedFormats.includes(fileExtension)
  ) {
    return "invalidFormat";
  }

  if (file.size > MAX_SIZE_BEFORE_COMPRESSION) {
    return "tooLarge";
  }

  return null;
};

/**
 * Deletes an image from Cloudinary given its URL.
 * Note: This requires an authenticated API call (not possible from frontend for security reasons).
 * This function should call a backend endpoint or Cloud Function that performs the deletion.
 * For now, it logs a warning.
 */
export async function deleteCloudinaryImage(imageUrl: string): Promise<void> {
  // Extract public_id from the URL
  try {
    const matches = imageUrl.match(/\/upload\/.*?\/([^/.]+)\./);
    const publicId = matches ? matches[1] : null;
    if (!publicId) {
      console.warn(
        "Could not extract public_id from Cloudinary URL:",
        imageUrl
      );
      return;
    }

    const deleteImage = httpsCallable(functions, "deleteProfilePicture");
    await deleteImage({ publicId });
  } catch (err) {
    console.error("Error in deleteCloudinaryImage:", err);
  }
}
